import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText, Shield, AlertOctagon, FileCheck } from "lucide-react";
import PublicFooter from "../components/PublicFooter";
import LastUpdatedBadge from "../components/LastUpdatedBadge";
import usePageMeta from "../hooks/usePageMeta";

const CLAUSES = [
  {
    icon: ScrollText,
    title: "What Fortiqa provides",
    body: [
      "Digital merchant protection: cover for cyber-related losses (M-Pesa fraud, scam orders, account compromise, social-media takeover).",
      "Claim intake: web, WhatsApp FlashClaim, and merchant portal channels.",
      "Risk scoring: an AI-driven CyberScore that informs underwriting and claim decisions.",
      "Payment verification: reconciliation of premiums with M-Pesa, CapitalPay, and partner banks.",
    ],
  },
  {
    icon: FileCheck,
    title: "When cover starts and ends",
    body: [
      "Cover begins only after a successful premium payment and policy activation in your Fortiqa dashboard.",
      "Each policy has a stated start date, end date, and claim limit. Cover does not extend retroactively to incidents before the start date.",
      "Renewals may be automatic or manual depending on your tier — check your dashboard for the next-payment date.",
    ],
  },
  {
    icon: Shield,
    title: "How claims are decided",
    body: [
      "Claims may be auto-approved (FlashClaim), rejected, or routed for human review based on the policy terms, your CyberScore, the evidence you provide, and any fraud signals our system raises.",
      "We may request additional evidence or clarification. Failure to respond within the stated window may close the claim.",
      "Auto-approved claims are paid via the same channel you used to pay your premium, where possible.",
    ],
  },
  {
    icon: AlertOctagon,
    title: "Suspension and termination",
    body: [
      "Fortiqa may suspend or terminate merchants for suspected fraud, false claims, misuse of the service, breach of these terms, or persistent non-payment.",
      "Where suspension is applied, you will be notified by the contact channel on file and given a chance to respond, except where investigation requires confidentiality.",
    ],
  },
  {
    icon: FileCheck,
    title: "Pricing",
    body: [
      "Indicative pricing: KES 99 per week via M-Pesa, unless changed in your merchant dashboard or via specific offer terms.",
      "Tiered pricing (Social Vendor Pro, Enterprise SME) is shown in your dashboard. Prices may change with notice — current prices are always those displayed in your dashboard at the time of purchase or renewal.",
    ],
  },
];

export default function Terms() {
  usePageMeta(
    "Terms · Fortiqa",
    "Fortiqa terms of service: what we provide, when cover starts, how claims are decided, and pricing.",
  );
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#114232]" data-testid="terms-page">
      <header className="px-6 lg:px-10 py-6 max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-xs text-[#5c6360] hover:text-[#114232] inline-flex items-center gap-1"
              data-testid="back-to-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Fortiqa
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#c15337]">Terms</div>
      </header>
      <main className="px-6 lg:px-10 max-w-3xl mx-auto py-4">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Terms of service</h1>
        <div className="mb-4"><LastUpdatedBadge pageKey="terms" /></div>
        <p className="text-sm sm:text-base text-[#5c6360] mb-8 max-w-2xl">
          These terms cover your use of Fortiqa as a merchant. Please read
          carefully — your acceptance is required before any policy can be
          issued.
        </p>

        {CLAUSES.map((c, idx) => (
          <section key={c.title} className="bg-white border border-[#114232]/10 rounded-xl p-5 sm:p-6 mb-4"
                    data-testid={`terms-clause-${idx + 1}`}>
            <div className="flex items-center gap-2.5 mb-3">
              <c.icon className="w-5 h-5 text-[#c15337]" />
              <h2 className="font-heading text-lg sm:text-xl font-bold">{idx + 1}. {c.title}</h2>
            </div>
            <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-[#1b1d1c]">
              {c.body.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </section>
        ))}

        <section className="bg-[#d49a36]/8 border border-[#d49a36]/30 rounded-xl p-5 sm:p-6 mb-8"
                  data-testid="terms-disclaimer">
          <h2 className="font-heading text-lg sm:text-xl font-bold mb-2 text-[#8a5a16]">Important disclaimer</h2>
          <p className="text-sm sm:text-base text-[#1b1d1c]">
            Fortiqa is subject to final regulatory approval, insurer /
            underwriter agreements, and applicable Kenyan law. Some product
            features may be limited until those agreements are in place.
          </p>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
